<template>
  <section id="works" class="section works">
    <div class="container">
      <div class="works__head">
        <div class="section-head" v-reveal>
          <span class="eyebrow">04 / Works</span>
          <h2 class="section-title">
            <span class="en">Works</span>
            実績
          </h2>
        </div>
        <p class="works__lead" v-reveal="80">
          守秘義務の都合で、お客様の案件は公開していません。かわりに、私たち自身が業務で使うために作り、実際に動かしてきたシステムを 3 件公開します。いずれも社内案件のため、設計から運用までの実際の判断をそのまま書いています。
        </p>
      </div>

      <div class="works__list">
        <!-- 事例 A -->
        <article class="work" v-reveal="60">
          <div class="work__head">
            <span class="work__idx">事例 A</span>
            <h3 class="work__title">毎朝の AI ニュースを全自動で記事化・公開するシステム</h3>
          </div>
          <dl class="work__dl">
            <div class="work__row">
              <dt>業種 / 規模</dt>
              <dd>自社案件（社内メディア運用の自動化）</dd>
            </div>
            <div class="work__row">
              <dt>ご相談内容・課題</dt>
              <dd>情報収集と記事化を毎日続けるには人手がかかりすぎる。かといって自動生成をそのまま公開すると、品質を担保できない。</dd>
            </div>
            <div class="work__row">
              <dt>私たちの対応 / 使用技術</dt>
              <dd>収集から公開までを一本のパイプラインとして設計しました。RSS で記事を収集 → ローカル LLM で選別 → Claude で本文を執筆 → 9 項目の機械チェック（ゲート）→ 公開。実行基盤は VPS（Linux / systemd）と Mac（launchd）に分けています。チェックを 1 つでも通らなかった記事は公開されません（fail-closed）。</dd>
            </div>
            <div class="work__row">
              <dt>成果</dt>
              <dd>2026-08-25 に稼働を開始し、以降は毎日、人手を介さずに記事が公開されています。人が行うのは公開後の内容確認のみです。</dd>
            </div>
            <div class="work__row">
              <dt>期間 / 体制</dt>
              <dd>自社開発 / 少人数（設計・実装・運用とも社内）</dd>
            </div>
          </dl>
        </article>

        <!-- 事例 B -->
        <article class="work" v-reveal="60">
          <div class="work__head">
            <span class="work__idx">事例 B</span>
            <h3 class="work__title">株価データを対象とした研究用システム（社内 R&amp;D）</h3>
          </div>
          <dl class="work__dl">
            <div class="work__row">
              <dt>業種 / 規模</dt>
              <dd>自社案件（社内研究開発）</dd>
            </div>
            <div class="work__row">
              <dt>ご相談内容・課題</dt>
              <dd>検証の甘い分析は、もっともらしい結論を出してしまう。「間違った結果を出さない」ことを仕組みで担保できるか。</dd>
            </div>
            <div class="work__row">
              <dt>私たちの対応 / 使用技術</dt>
              <dd>
                データ品質と検証の設計に重心を置いて構築しました。
                <ul class="work__points">
                  <li>自動テスト 1,229 本を常時実行し、テストが落ちたら処理を止める（fail-closed）構成にしました</li>
                  <li>Point-in-Time でデータを扱い、その時点で入手できなかった情報を混入させない設計にしました</li>
                  <li>統計的な判定ゲートを置き、基準を満たさない結果は先へ進めない仕組みにしました</li>
                  <li>本番と同じ入力で結果だけを記録する方式（実発注を伴わない）で検証しました</li>
                </ul>
              </dd>
            </div>
            <div class="work__row">
              <dt>成果</dt>
              <dd>検証の網（自動テスト 1,229 本）と、基準を満たさない結果を通さないゲート構成を組み、一定期間にわたって稼働させました。</dd>
            </div>
            <div class="work__row">
              <dt>期間 / 体制</dt>
              <dd>自社開発・稼働終了 / 少人数</dd>
            </div>
          </dl>
          <p class="work__disclaimer">
            <span class="work__disclaimer-mark" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.6" />
                <path d="M10 5.6v5.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                <circle cx="10" cy="14.1" r="1" fill="currentColor" />
              </svg>
            </span>
            本事例は当社の研究・開発記録であり、投資勧誘・投資助言ではありません。投資判断はご自身の責任で行ってください。運用成績・銘柄・売買判断は公開しておりません。
          </p>
        </article>

        <!-- 事例 C -->
        <article class="work" v-reveal="60">
          <div class="work__head">
            <span class="work__idx">事例 C</span>
            <h3 class="work__title">縦型ショート動画の制作パイプライン</h3>
          </div>
          <dl class="work__dl">
            <div class="work__row">
              <dt>業種 / 規模</dt>
              <dd>自社案件（社内の映像制作の自動化）</dd>
            </div>
            <div class="work__row">
              <dt>ご相談内容・課題</dt>
              <dd>動画は継続的に出したいが、1 本ごとの制作コストが見合わない。</dd>
            </div>
            <div class="work__row">
              <dt>私たちの対応 / 使用技術</dt>
              <dd>台本から書き出しまでをコード化しました。Remotion（React による動画生成）でレイアウトとテロップを組み、ナレーションは本人の同意のもとで作成した音声クローンで生成。書き出した音声は whisper による文字起こしで全数を台本と突き合わせ、読み間違い・欠落を機械的に検出しています。</dd>
            </div>
            <div class="work__row">
              <dt>成果</dt>
              <dd>生成にかかる外部サービス利用料は 1 本あたり約 50 円（リテイク込みの実測）。これまでに 8 本を制作しました。</dd>
            </div>
            <div class="work__row">
              <dt>期間 / 体制</dt>
              <dd>自社開発 / 少人数</dd>
            </div>
          </dl>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'WorksSection',
}
</script>

<style scoped lang="scss">
.works {
  border-top: 1px solid var(--line);
}

.works__head {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: end;
}

.works__lead {
  max-width: 46ch;
  font-size: clamp(1rem, 1.3vw, 1.1rem);
  line-height: 1.9;
  color: var(--ink-soft);
}

.works__list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: clamp(44px, 6vw, 72px);
}

.work {
  position: relative;
  padding: clamp(28px, 3vw, 40px);
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: border-color 0.4s var(--ease), box-shadow 0.4s var(--ease);
}

.work::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 3px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.45s var(--ease);
}

.work:hover {
  border-color: var(--accent-line);
  box-shadow: 0 40px 70px -50px rgba(83, 48, 27, 0.55);
}

.work:hover::before {
  transform: scaleX(1);
}

.work__head {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--line);
}

.work__idx {
  font-family: var(--font-jp);
  font-weight: 500;
  font-size: 0.74rem;
  letter-spacing: 0.16em;
  color: var(--accent);
}

.work__title {
  font-family: var(--font-jp);
  font-weight: 700;
  font-size: clamp(1.15rem, 1.9vw, 1.42rem);
  line-height: 1.45;
  color: var(--ink);
}

.work__row {
  display: grid;
  grid-template-columns: 168px 1fr;
  gap: 20px;
  padding: 18px 0;
  border-bottom: 1px solid var(--line-soft);

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.work__row dt {
  font-family: var(--font-jp);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.7;
  color: var(--muted);
  padding-top: 0.24em;
}

.work__row dd {
  font-size: 0.94rem;
  line-height: 1.9;
  color: var(--ink-soft);
}

.work__points {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.work__points li {
  position: relative;
  padding-left: 18px;
}

.work__points li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.78em;
  width: 6px;
  height: 6px;
  background: var(--accent);
  transform: rotate(45deg);
}

.work__disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 24px;
  padding: 16px 18px;
  background: var(--paper-sunk);
  border-left: 2px solid var(--accent-line);
  border-radius: var(--radius);
  font-size: 0.84rem;
  line-height: 1.85;
  color: var(--ink-soft);
}

.work__disclaimer-mark {
  flex: 0 0 auto;
  display: inline-flex;
  color: var(--accent);
  margin-top: 0.3em;
}

@media (max-width: 760px) {
  .works__head {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .work__row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
